import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CustomerOverViewService } from '../../customer-overview.service';
import { NotificationType } from '~core/enum/notification.enum';
import { TablesTranslations } from '~shared/components/tables/tables-translations.service';
import { Contact, Customer, SidebarOptions } from '~core/models';
import { ContactManagementService, CustomerService, DefaultNotificationService, NavigationService, ScreenSizeService } from '~core/services';
import { ContactManagementTranslationsService, TooltipTranslationService } from '~shared/translations';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { SidebarService } from '~core/components/sidebar/sidebar.service';

/**
 * This component is the page that views only the last contacts
 */
@UntilDestroy()
@Component({
  selector: 'app-contact-management-page',
  templateUrl: './contact-management-page.component.html',
  styleUrls: ['../../customer-overview.component.scss']
})
export class ContactManagementPageComponent implements OnInit, OnDestroy {
  public latestContacts: Array<Contact>;
  public customer: Customer;
  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];

  public baseApi: string;
  public customQueries: Array<any> = [];

  constructor(
    public customerOverviewService: CustomerOverViewService,
    public customerService: CustomerService,
    public sidebarService: SidebarService,
    public contactManagementService: ContactManagementService,
    public navigationService: NavigationService,
    private notificationService: DefaultNotificationService,
    public buttonsTranslations: ButtonsTranslations,
    public contactManagementTranslations: ContactManagementTranslationsService,
    public tooltipTranslations: TooltipTranslationService,
    public tablesTranslations: TablesTranslations,
    private screenSizeService: ScreenSizeService,

  ) {
    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
    }
    this.navigationService.setCurrentPage('lastContacts');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'customerProfile']);
    this.contactManagementService.castContactList.pipe(untilDestroyed(this))
      .subscribe((data: Array<Contact>) => {
        if (data) {
          this.latestContacts = data;
        }
      });
    this.customerService.castSelectedCustomer.pipe(untilDestroyed(this))
      .subscribe(customer => this.customer = customer);
    this.columns.push(
      '', this.tablesTranslations.i18nDate, this.tablesTranslations.i18nTitle,
      this.tablesTranslations.i18nComment, this.tablesTranslations.i18nAuthor
    );
    this.keys.push(['pinned', 'updatedAt', 'title', 'comment', 'fullName']);
  }

  public ngOnInit(): void {
    this.getContactEndpoint(this.customer.id);
  }

  public ngOnDestroy(): void {
    this.customerOverviewService.toggleAllModal(false);
  }

  /**
   * Get the contacts for the current customer.
   */
  public getContactManagementById(): any {
    this.contactManagementService.getById(this.customer.id).pipe(untilDestroyed(this))
      .subscribe({
        next: (res: Array<Contact>) => {
          this.contactManagementService.setContactList(res);
        },
        error: (error: Error) => {
          this.notificationService.showNotification({
            type: NotificationType.ALERT,
            message: this.contactManagementTranslations.i18nErrorFetchingContacts
          });
        }
      });
  }

  /**
   * Announce / notify change with activity history service.
   */
  public createNewContact(): void {
    this.contactManagementService.clearContactEdited();
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleNewContactModal(true);
  }

  /**
   * Fill the contact form with user/admin and contact data
   */
  public fillContact(contact: Contact): void {
    this.contactManagementService.announceContactEdit(contact);
    this.customerOverviewService.toggleAllModal(false);
    this.customerOverviewService.toggleEditContactModal(true);
  }

  public setNewReminderList(reminders: Array<any>): any {
    const data: Array<Contact> = this.contactManagementService.createContactClass(reminders);
    this.contactManagementService.setContactList(data);
  }

  /**
   * Get the tixx select submissions for the current customer.
   */
  public getContactEndpoint(id: number): any {
    const endpoint = this.contactManagementService.getAllCustomerContactsEndpoint(id);
    this.baseApi = endpoint[0];
    this.customQueries = endpoint[1];
  }
}
